"use client";

import { useEffect, useMemo, useState } from "react";
import AuthenticatedLayout from "./../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "./../../Components/GroupedContainer";
import axios from "axios";
import Swal from "sweetalert2";
import { Plus, Trash2, Save, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  TbHash,
  TbFilter,
  TbUser,
  TbClipboardText,
  TbBuilding,
  TbMapPin,
  TbCalendar,
  TbCurrencyDollar,
  TbCar,
  TbChevronDown,
  TbCheckbox,
  TbX,
  TbCircleCheck,
  TbCircleDashed
} from "react-icons/tb";

function NewExternalLoad() {
  const navigate = useNavigate();
  const API_PREFIX = process.env.REACT_APP_API_PREFIX;

  // Load Status: SOMENTE (2, 4, 6, 7, 8)
  const loadStatuses = useMemo(
    () => [
      { id: 2, name: "Assigned" },
      { id: 4, name: "Picked Up" },
      { id: 6, name: "Delivered" },
      { id: 7, name: "Billed" },
      { id: 8, name: "Paid" },
    ],
    []
  );

  const paymentTypeOptions = useMemo(
    () => ["COD", "COP", "2 BD", "5 BD", "7 BD", "15 BD", "30 BD"],
    []
  );

  const paymentMethodOptions = useMemo(
    () => [
      "ACH",
      "Check",
      "Zelle",
      "Venmo",
      "Cashapp",
      "Uship",
      "Cash",
      "PayPal",
      "Credit card",
      "Debit",
      "Comcheck",
      "Echeck",
      "Electronic Check",
    ],
    []
  );

  const api = useMemo(() => {
    if (!API_PREFIX) {
      console.error("Missing REACT_APP_API_PREFIX in .env");
    }

    const baseURL = (API_PREFIX || "").replace(/\/+$/, "");
    const instance = axios.create({ baseURL });

    instance.interceptors.request.use((config) => {
      const userToken = localStorage.getItem("token");
      config.headers = config.headers || {};
      config.headers["Screen-Name"] = "/Load";
      if (userToken) config.headers.Authorization = `Bearer ${userToken}`;
      return config;
    });

    return instance;
  }, [API_PREFIX]);

  const [drivers, setDrivers] = useState([]);
  const [carTypes, setCarTypes] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [orderId, setOrderId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [loadStatusId, setLoadStatusId] = useState("2");
  const [loadboard, setLoadboard] = useState("");
  const [pickupCity, setPickupCity] = useState("");
  const [pickupState, setPickupState] = useState("");
  const [pickupZip, setPickupZip] = useState("");
  const [deliveryCity, setDeliveryCity] = useState("");
  const [deliveryState, setDeliveryState] = useState("");
  const [deliveryZip, setDeliveryZip] = useState("");
  const [paymentRate, setPaymentRate] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [brokerFee, setBrokerFee] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [dropoffDate, setDropoffDate] = useState("");

  const [items, setItems] = useState([
    {
      year: "",
      make: "",
      model: "",
      car_type_id: "",
      is_operable: true,
    },
  ]);

  const [errors, setErrors] = useState(null);

  const unwrapApiData = (res) => res?.data?.data ?? res?.data;
  const extractErrorPayload = (err) => {
    const payload = err?.response?.data;
    return payload || { message: "Unexpected error" };
  };

  const formatMoneyString = (value) => {
    if (value === null || value === undefined) return "";
    const raw = String(value).trim();
    if (raw === "") return "";
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) return "";
    return parsed.toFixed(2);
  };

  const handleMoneyBlur = (setter) => (e) => {
    setter(formatMoneyString(e.target.value));
  };

  const clearField = (setter) => () => setter("");

  useEffect(() => {
    const fetchRefs = async () => {
      setLoadingRefs(true);
      try {
        try {
          const resDrivers = await api.get("https://new.cajuu.app/api/driver");
          const dataDrivers = unwrapApiData(resDrivers);
          const listDrivers = Array.isArray(dataDrivers) ? dataDrivers : dataDrivers?.data;
          setDrivers(Array.isArray(listDrivers) ? listDrivers : []);
        } catch (e) {
          setDrivers([]);
        }

        try {
          const resCarTypes = await api.get("https://new.cajuu.app/api/cartype");
          const dataCarTypes = unwrapApiData(resCarTypes);
          const listCarTypes = Array.isArray(dataCarTypes) ? dataCarTypes : dataCarTypes?.data;
          setCarTypes(Array.isArray(listCarTypes) ? listCarTypes : []);
        } catch (e) {
          setCarTypes([]);
        }
      } finally {
        setLoadingRefs(false);
      }
    };

    fetchRefs();
  }, [api]);

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      { year: "", make: "", model: "", car_type_id: "", is_operable: true },
    ]);
  };

  const removeItem = (index) => {
    setItems((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const updateItem = (index, key, value) => {
    setItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, [key]: value } : it))
    );
  };

  const validateClientSide = () => {
    const errs = {};

    if (!orderId.trim()) errs.order_id = ["Order ID is required."];
    if (!pickupCity.trim()) errs.pickup_city = ["Pickup city is required."];
    if (!pickupState.trim()) errs.pickup_state = ["Pickup state is required."];
    if (!deliveryCity.trim()) errs.delivery_city = ["Delivery city is required."];
    if (!deliveryState.trim()) errs.delivery_state = ["Delivery state is required."];

    if (paymentRate === "" || Number(paymentRate) < 0) {
      errs.payment_rate = ["Payment rate is required and must be >= 0."];
    }

    if (!Array.isArray(items) || items.length < 1) {
      errs.load_items = ["At least one vehicle is required."];
    } else {
      items.forEach((it, idx) => {
        if (!String(it.year || "").trim()) errs[`load_items.${idx}.year`] = ["Year is required."];
        if (!String(it.make || "").trim()) errs[`load_items.${idx}.make`] = ["Make is required."];
        if (!String(it.model || "").trim()) errs[`load_items.${idx}.model`] = ["Model is required."];
        if (!String(it.car_type_id || "").trim()) errs[`load_items.${idx}.car_type_id`] = ["Car type is required."];
      });
    }

    setErrors(Object.keys(errs).length ? errs : null);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrors(null);

    if (!validateClientSide()) {
      Swal.fire({
        icon: "error",
        title: "Validation error",
        text: "Please fix the highlighted fields.",
      });
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        order_id: orderId.trim(),
        driver_id: driverId ? Number(driverId) : null,
        load_status_id: loadStatusId ? Number(loadStatusId) : null,
        loadboard: loadboard ? loadboard.trim() : null,
        pickup_city: pickupCity.trim(),
        pickup_state: pickupState.trim(),
        pickup_zipcode: pickupZip ? pickupZip.trim() : null,
        delivery_city: deliveryCity.trim(),
        delivery_state: deliveryState.trim(),
        delivery_zipcode: deliveryZip ? deliveryZip.trim() : null,
        payment_rate: Number(formatMoneyString(paymentRate)),
        payment_type: paymentType ? paymentType.trim() : null,
        payment_method: paymentMethod ? paymentMethod.trim() : null,
        broker_fee: brokerFee !== "" && brokerFee !== null ? Number(formatMoneyString(brokerFee)) : null,
        pickup_date: pickupDate ? pickupDate : null,
        dropoff_date: dropoffDate ? dropoffDate : null,
        load_items: items.map((it) => ({
          year: String(it.year).trim(),
          make: String(it.make).trim(),
          model: String(it.model).trim(),
          car_type_id: Number(it.car_type_id),
          is_operable: Boolean(it.is_operable),
        })),
      };

      const res = await api.post("/externalload", payload);
      const created = unwrapApiData(res);

      Swal.fire({
        icon: "success",
        title: "External Load created",
        text: `External Load #${created?.id ?? ""} created successfully.`,
      });

      navigate("/finance");
    } catch (err) {
      const payload = extractErrorPayload(err);
      const serverErrors = payload?.message && typeof payload.message === "object"
        ? payload.message
        : payload?.data && typeof payload.data === "object"
          ? payload.data
          : null;

      if (serverErrors) setErrors(serverErrors);

      Swal.fire({
        icon: "error",
        title: "Failed to create",
        text: payload?.data?.toString?.() || payload?.message?.toString?.() || "Unexpected error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const FieldError = ({ name }) => {
    if (!errors) return null;
    const msg = errors?.[name];
    if (!msg) return null;
    const text = Array.isArray(msg) ? msg.join(" ") : String(msg);
    return <div className="text-red-400 text-xs mt-1">{text}</div>;
  };

  const driverLabel = (d) => {
    const u = d?.user;
    const first = u?.first_name || "";
    const last = u?.last_name || "";
    const full = `${first} ${last}`.trim();
    if (full) return `${full} (#${d.id})`;
    if (u?.email) return `${u.email} (#${d.id})`;
    return `Driver #${d.id}`;
  };

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <div className="flex items-center justify-between gap-3">
            <InfoSection
              title="NEW EXTERNAL LOAD"
              description="Create a new External Load"
            />

          </div>
        </GroupedContainer>

        <form onSubmit={submit} className="space-y-8 mt-6">
          {/* ===== Basic Information ===== */}
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white mb-2">Basic Information</h2>
              <div className="h-px w-full bg-zinc-700"></div>
            </div>

            <div className="bg-[#1c1b19] p-6 rounded-xl border border-zinc-700">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Order ID */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Order ID <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbHash className="text-gray-400 text-lg" />
                    </div>
                    <input
                      className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      value={orderId}
                      onChange={(e) => setOrderId(e.target.value)}
                      placeholder="Enter Order ID"
                    />
                    {orderId && (
                      <button
                        onClick={clearField(setOrderId)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                      >
                        <TbX className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <FieldError name="order_id" />
                </div>

                {/* Load Status */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Load Status
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbFilter className="text-amber-500 text-lg" />
                    </div>
                    <select
                      className="w-full pl-10 pr-12 bg-[#292826] text-white py-3 text-sm border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                      value={loadStatusId}
                      onChange={(e) => setLoadStatusId(e.target.value)}
                    >
                      {loadStatuses.map((s) => (
                        <option key={s.id} value={s.id} className="bg-[#1c1b19]">
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                      <TbChevronDown className="text-gray-400 text-lg" />
                    </div>
                  </div>
                </div>

                {/* Driver */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Driver (optional)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbUser className="text-gray-400 text-lg" />
                    </div>
                    <select
                      className="w-full pl-10 pr-12 bg-[#292826] text-white py-3 text-sm border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                      value={driverId}
                      onChange={(e) => setDriverId(e.target.value)}
                      disabled={loadingRefs}
                    >
                      <option value="" className="bg-[#1c1b19]">-- Select --</option>
                      {drivers.map((d) => (
                        <option key={d.id} value={d.id} className="bg-[#1c1b19]">
                          {driverLabel(d)}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                      <TbChevronDown className="text-gray-400 text-lg" />
                    </div>
                  </div>
                  <FieldError name="driver_id" />
                </div>

                {/* Loadboard */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Loadboard (optional)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbClipboardText className="text-gray-400 text-lg" />
                    </div>
                    <input
                      className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      value={loadboard}
                      onChange={(e) => setLoadboard(e.target.value)}
                      placeholder="Central Dispatch / Super Dispatch"
                    />
                    {loadboard && (
                      <button
                        onClick={clearField(setLoadboard)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                      >
                        <TbX className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ===== Pickup & Delivery ===== */}
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white mb-2">Pickup & Delivery</h2>
              <div className="h-px w-full bg-zinc-700"></div>
            </div>

            <div className="bg-[#1c1b19] p-6 rounded-xl border border-zinc-700">
              {/* Pickup Section */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-white mb-4">Pickup Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Pickup City */}
                  <div>
                    <label className="text-sm font-medium text-white mb-2 block">
                      Pickup City <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <TbBuilding className="text-gray-400 text-lg" />
                      </div>
                      <input
                        className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                        value={pickupCity}
                        onChange={(e) => setPickupCity(e.target.value)}
                        placeholder="City"
                      />
                      {pickupCity && (
                        <button
                          onClick={clearField(setPickupCity)}
                          className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                        >
                          <TbX className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <FieldError name="pickup_city" />
                  </div>

                  {/* Pickup State */}
                  <div>
                    <label className="text-sm font-medium text-white mb-2 block">
                      Pickup State <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <TbMapPin className="text-gray-400 text-lg" />
                      </div>
                      <input
                        className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                        value={pickupState}
                        onChange={(e) => setPickupState(e.target.value)}
                        placeholder="State (e.g., GA)"
                      />
                      {pickupState && (
                        <button
                          onClick={clearField(setPickupState)}
                          className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                        >
                          <TbX className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <FieldError name="pickup_state" />
                  </div>

                  {/* Pickup Zip */}
                  <div>
                    <label className="text-sm font-medium text-white mb-2 block">
                      Pickup Zip (optional)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <TbHash className="text-gray-400 text-lg" />
                      </div>
                      <input
                        className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                        value={pickupZip}
                        onChange={(e) => setPickupZip(e.target.value)}
                        placeholder="Zip Code"
                      />
                      {pickupZip && (
                        <button
                          onClick={clearField(setPickupZip)}
                          className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                        >
                          <TbX className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Pickup Date */}
                  <div>
                    <label className="text-sm font-medium text-white mb-2 block">
                      Pickup Date (optional)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <TbCalendar className="text-blue-500 text-lg" />
                      </div>
                      <input
                        type="date"
                        value={pickupDate}
                        onChange={(e) => setPickupDate(e.target.value)}
                        onFocus={(e) => e.target.showPicker()}
                        style={{ colorScheme: "dark" }}
                        className="w-full pl-10 pr-4 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Section */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Delivery Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Delivery City */}
                  <div>
                    <label className="text-sm font-medium text-white mb-2 block">
                      Delivery City <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <TbBuilding className="text-gray-400 text-lg" />
                      </div>
                      <input
                        className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                        value={deliveryCity}
                        onChange={(e) => setDeliveryCity(e.target.value)}
                        placeholder="City"
                      />
                      {deliveryCity && (
                        <button
                          onClick={clearField(setDeliveryCity)}
                          className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                        >
                          <TbX className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <FieldError name="delivery_city" />
                  </div>

                  {/* Delivery State */}
                  <div>
                    <label className="text-sm font-medium text-white mb-2 block">
                      Delivery State <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <TbMapPin className="text-gray-400 text-lg" />
                      </div>
                      <input
                        className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                        value={deliveryState}
                        onChange={(e) => setDeliveryState(e.target.value)}
                        placeholder="State (e.g., FL)"
                      />
                      {deliveryState && (
                        <button
                          onClick={clearField(setDeliveryState)}
                          className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                        >
                          <TbX className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <FieldError name="delivery_state" />
                  </div>

                  {/* Delivery Zip */}
                  <div>
                    <label className="text-sm font-medium text-white mb-2 block">
                      Delivery Zip (optional)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <TbHash className="text-gray-400 text-lg" />
                      </div>
                      <input
                        className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                        value={deliveryZip}
                        onChange={(e) => setDeliveryZip(e.target.value)}
                        placeholder="Zip Code"
                      />
                      {deliveryZip && (
                        <button
                          onClick={clearField(setDeliveryZip)}
                          className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                        >
                          <TbX className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Dropoff Date */}
                  <div>
                    <label className="text-sm font-medium text-white mb-2 block">
                      Dropoff Date (optional)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <TbCalendar className="text-blue-500 text-lg" />
                      </div>
                      <input
                        type="date"
                         style={{ colorScheme: "dark" }}
                        className="w-full pl-10 pr-4 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      
                        value={dropoffDate}
                        onChange={(e) => setDropoffDate(e.target.value)}
                        onFocus={(e) => e.target.showPicker()}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ===== Payment ===== */}
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white mb-2">Payment</h2>
              <div className="h-px w-full bg-zinc-700"></div>
            </div>

            <div className="bg-[#1c1b19] p-6 rounded-xl border border-zinc-700">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Payment Rate */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Payment Rate ($) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbCurrencyDollar className="text-gray-400 text-lg" />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      value={paymentRate}
                      onChange={(e) => setPaymentRate(e.target.value)}
                      onBlur={handleMoneyBlur(setPaymentRate)}
                      placeholder="0.00"
                    />
                    {paymentRate && (
                      <button
                        onClick={clearField(setPaymentRate)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                      >
                        <TbX className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <FieldError name="payment_rate" />
                </div>

                {/* Broker Fee */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Broker Fee ($) (optional)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbCurrencyDollar className="text-gray-400 text-lg" />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      value={brokerFee}
                      onChange={(e) => setBrokerFee(e.target.value)}
                      onBlur={handleMoneyBlur(setBrokerFee)}
                      placeholder="0.00"
                    />
                    {brokerFee && (
                      <button
                        onClick={clearField(setBrokerFee)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                      >
                        <TbX className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Payment Type */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Payment Type (optional)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbFilter className="text-amber-500 text-lg" />
                    </div>
                    <select
                      className="w-full pl-10 pr-12 bg-[#292826] text-white py-3 text-sm border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                      value={paymentType}
                      onChange={(e) => setPaymentType(e.target.value)}
                    >
                      <option value="" className="bg-[#1c1b19]">-- Select --</option>
                      {paymentTypeOptions.map((opt) => (
                        <option key={opt} value={opt} className="bg-[#1c1b19]">
                          {opt}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                      <TbChevronDown className="text-gray-400 text-lg" />
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Payment Method (optional)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <TbFilter className="text-amber-500 text-lg" />
                    </div>
                    <select
                      className="w-full pl-10 pr-12 bg-[#292826] text-white py-3 text-sm border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      <option value="" className="bg-[#1c1b19]">-- Select --</option>
                      {paymentMethodOptions.map((opt) => (
                        <option key={opt} value={opt} className="bg-[#1c1b19]">
                          {opt}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                      <TbChevronDown className="text-gray-400 text-lg" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ===== Vehicles ===== */}
          <div>
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Vehicles</h2>
                <button
                  type="button"
                  onClick={addItem}
                  className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium transition-colors"
                >
                  <Plus size={18} />
                  Add Vehicle
                </button>
              </div>
              <div className="h-px w-full bg-zinc-700 mt-2"></div>
            </div>

            <div className="space-y-4">
              {items.map((it, idx) => (
                <div key={idx} className="bg-[#1c1b19] rounded-xl border border-zinc-700 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-lg font-semibold text-white">Vehicle #{idx + 1}</div>
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-zinc-700 hover:bg-zinc-900 transition-colors text-white disabled:opacity-50"
                      disabled={items.length <= 1}
                      title={items.length <= 1 ? "At least one vehicle is required" : "Remove"}
                    >
                      <Trash2 size={16} />
                      Remove
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                    {/* Year */}
                    <div>
                      <label className="text-sm font-medium text-white mb-2 block">
                        Year <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                          <TbCalendar className="text-gray-400 text-lg" />
                        </div>
                        <input
                          className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                          value={it.year}
                          onChange={(e) => updateItem(idx, "year", e.target.value)}
                          placeholder="2018"
                        />
                        {it.year && (
                          <button
                            onClick={() => updateItem(idx, "year", "")}
                            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                          >
                            <TbX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <FieldError name={`load_items.${idx}.year`} />
                    </div>

                    {/* Make */}
                    <div>
                      <label className="text-sm font-medium text-white mb-2 block">
                        Make <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                          <TbCar className="text-gray-400 text-lg" />
                        </div>
                        <input
                          className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                          value={it.make}
                          onChange={(e) => updateItem(idx, "make", e.target.value)}
                          placeholder="Toyota"
                        />
                        {it.make && (
                          <button
                            onClick={() => updateItem(idx, "make", "")}
                            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                          >
                            <TbX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <FieldError name={`load_items.${idx}.make`} />
                    </div>

                    {/* Model */}
                    <div>
                      <label className="text-sm font-medium text-white mb-2 block">
                        Model <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                          <TbCar className="text-gray-400 text-lg" />
                        </div>
                        <input
                          className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                          value={it.model}
                          onChange={(e) => updateItem(idx, "model", e.target.value)}
                          placeholder="Camry"
                        />
                        {it.model && (
                          <button
                            onClick={() => updateItem(idx, "model", "")}
                            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                          >
                            <TbX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <FieldError name={`load_items.${idx}.model`} />
                    </div>

                    {/* Car Type */}
                    <div>
                      <label className="text-sm font-medium text-white mb-2 block">
                        Car Type <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                          <TbCar className="text-gray-400 text-lg" />
                        </div>
                        <select
                          className="w-full pl-10 pr-12 bg-[#292826] text-white py-3 text-sm border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                          value={it.car_type_id}
                          onChange={(e) => updateItem(idx, "car_type_id", e.target.value)}
                          disabled={loadingRefs || carTypes.length === 0}
                        >
                          <option value="" className="bg-[#1c1b19]">
                            {loadingRefs ? "Loading..." : "-- Select --"}
                          </option>
                          {carTypes.map((ct) => (
                            <option key={ct.id} value={ct.id} className="bg-[#1c1b19]">
                              {ct?.type ? ct.type : `Car Type #${ct.id}`}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                          <TbChevronDown className="text-gray-400 text-lg" />
                        </div>
                      </div>
                      <FieldError name={`load_items.${idx}.car_type_id`} />
                    </div>

                    {/* Operable */}
                    <div>
                      <label className="text-sm font-medium text-white mb-2 block">
                        Operable <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                          {it.is_operable ? (
                            <TbCircleCheck className="text-green-500 text-lg" />
                          ) : (
                            <TbCircleDashed className="text-red-500 text-lg" />
                          )}
                        </div>
                        <select
                          className="w-full pl-10 pr-12 bg-[#292826] text-white py-3 text-sm border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                          value={it.is_operable ? "1" : "0"}
                          onChange={(e) => updateItem(idx, "is_operable", e.target.value === "1")}
                        >
                          <option value="1" className="bg-[#1c1b19]">Yes</option>
                          <option value="0" className="bg-[#1c1b19]">No</option>
                        </select>
                        <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                          <TbChevronDown className="text-gray-400 text-lg" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <FieldError name="load_items" />
          </div>

          {/* ===== Action Buttons ===== */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 text-sm px-6 py-3 rounded-lg border border-zinc-700 hover:bg-zinc-900 transition-colors text-white font-medium"
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 text-sm px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 transition-colors text-white font-medium disabled:opacity-60"
              disabled={submitting}
            >
              <Save size={18} />
              {submitting ? "Saving..." : "Save External Load"}
            </button>
          </div>
        </form>

        {loadingRefs && (
          <div className="fixed bottom-2 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-3 py-1 rounded text-xs shadow-lg">
            Loading reference data...
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
}

export default NewExternalLoad;